interface Measure {
  bytes_in: number;
  bytes_out: number;
  packets_in: number;
  packets_out: number;
  date: string;
}

const NETWORK_DATA_RATIO = 0.7;

const getScaleMaximum = (observedMaximum:number, capacity?:number) => {
  const numericCapacity = Number(capacity);
  return Number.isFinite(numericCapacity) && numericCapacity > 0
    ? numericCapacity
    : Math.max(0, observedMaximum);
}

const getNetworkScaleMaximum = (observedMaximum:number) => (
  observedMaximum > 0 ? observedMaximum / NETWORK_DATA_RATIO : 0
)

export function calculateScales(data: Measure[],): any {
  const packetsScale: number[] = [];
  const bytesScale: number[] = [];

  if (data.length === 0) {
    return [packetsScale, bytesScale];
  }

  // Находим максимальные значения packets_in, packets_out, bytes_in и bytes_out
  const maxPacketsIn = Math.max(...data.map((measure) => measure.packets_in));
  const maxPacketsOut = Math.max(...data.map((measure) => measure.packets_out));
  const maxBytesIn = Math.max(...data.map((measure) => measure.bytes_in));
  const maxBytesOut = Math.max(...data.map((measure) => measure.bytes_out));

  // Определяем количество точек (в данном случае, 11)
  const numPoints = 8;

  // Вычисляем шаг для шкалы, используя максимальные значения из обоих полей
  const packetsMaximum = getNetworkScaleMaximum(Math.max(maxPacketsIn, maxPacketsOut));
  const bytesMaximum = getNetworkScaleMaximum(Math.max(maxBytesIn, maxBytesOut));
  const packetsStep = packetsMaximum / (numPoints - 1);
  const bytesStep = bytesMaximum / (numPoints - 1);

  // Создаем шкалы
  for (let i = 0; i < numPoints; i++) {
    const packetsValue = i * packetsStep;
    const bytesValue = i * bytesStep;

    const sizes = ['', 'K', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y']
    const sizes1 = ['', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    //@ts-ignore
    packetsScale.push(transformValues(packetsValue, 2,sizes));
    //@ts-ignore
    bytesScale.push(transformValues(bytesValue, 2 ,sizes1));
  }

  return [packetsScale?.reverse(), bytesScale?.reverse()];
}


export function generateCPUScaleData(cpuData:any) {
  if (!Array.isArray(cpuData) || cpuData.length === 0) {
    console.error("Данные отсутствуют или представлены в неправильном формате.");
    return;
  }

  // Находим максимальное значение в массиве данных
  const maxValue = Math.max(...cpuData?.map((meas:any) => meas.value));
  const numberOfTicks = Math.min(8, maxValue / 10 + 1);
  // Генерируем Y-шкалу в процентах от 0% до максимального значения
  const yScale = [];
  for (let i = 0; i < numberOfTicks; i += 10) {
    yScale.push((maxValue / numberOfTicks) * i);
  }
  return yScale.reverse();
}

export function convertToPercentageCpu(dataArray:any, capacity?:number) {
  if (dataArray.length === 0) {
    return [];
  }
  const sortedData = [...dataArray].sort((a, b) => (new Date(a.date)).getTime() - (new Date(b.date)).getTime());
  const observedMaximum = Math.max(...sortedData.map((measure:any) => measure.value));
  const scaleMaximum = getScaleMaximum(observedMaximum, capacity);
  const step = sortedData.length === 1 ? 0 : 100 / (sortedData.length - 1);

  return sortedData.map((metric:any, index:number) => {
    const normalizedValue = scaleMaximum > 0 ? Math.min(100, Math.max(0, (metric.value / scaleMaximum) * 100)) : 0;
    return [index * step, normalizedValue.toFixed(1), metric.date, `${metric.value.toFixed(2)}%`]
  });
}

export const formatRamValue = (value:number) => {
  if (value >= 1024) {
    return `${Number((value / 1024).toFixed(2))} GB`;
  }

  return `${Number(value.toFixed(2))} MB`;
};

export function generateUsageScale(dataArray:any[] = [], capacity?:number) {
  if (!dataArray.length) {
    return [];
  }

  const observedMaximum = Math.max(...dataArray.map((measure:any) => measure.value));
  const max = getScaleMaximum(observedMaximum, capacity);
  const ticks = 8;
  const step = ticks === 1 ? 0 : max / (ticks - 1);
  const scale = [];

  for (let index = 0; index < ticks; index++) {
    scale.push(formatRamValue(max - (step * index)));
  }

  return scale;
}

export function convertToUsageValues(dataArray:any[] = [], capacity?:number) {
  if (!dataArray.length) {
    return [];
  }

  const sortedData = [...dataArray].sort((a, b) => (new Date(a.date)).getTime() - (new Date(b.date)).getTime());
  const observedMaximum = Math.max(...sortedData.map((measure:any) => measure.value));
  const max = getScaleMaximum(observedMaximum, capacity);
  const stepX = sortedData.length === 1 ? 0 : 100 / (sortedData.length - 1);

  return sortedData.map((metric:any, index:number) => {
    const normalizedValue = max > 0 ? Math.min(100, Math.max(0, (metric.value / max) * 100)) : 0;

    return [
      index * stepX,
      normalizedValue.toFixed(1),
      metric.date,
      formatRamValue(metric.value),
    ];
  });
}

// export function generateYScale(dataArray:any[]) {
//   const dataLength = dataArray.length;
  
//   if (dataLength === 0) {
//     return [];
//   }

//   const max = Math.max(...dataArray?.map((meas:any) => meas.value));
//   let maxScale;

//   if (max < 150) {
//     maxScale = 10;
//   } else if (max < 300) {
//     maxScale = 50;
//   } else {
//     maxScale = 100;
//   }

//   const scaledData = [];
//   const step = Math.ceil(max / maxScale);
  
//   for (let i = 0; i <= dataLength; i++) {
//     const scaledValue = i * step;
//     scaledData.push(`${scaledValue}%`);
//   }

//   if (scaledData.length > 9) {
//     const stepSize = Math.ceil(scaledData.length / 9);
//     const scaledDataLimited = scaledData.filter((_, index) => index % stepSize === 0);
//     return scaledDataLimited.reverse();
//   }
//   return scaledData.reverse();
// }
export function generateYScale(percentArray: any[], capacity?:number): any[] {
  if (!percentArray.length) {
    return [];
  }

  const observedMaximum = Math.max(...percentArray.map((measure:any) => measure.value));
  const maxPercent = getScaleMaximum(observedMaximum, capacity);
  const ticks = 8;
  const step = maxPercent / (ticks - 1);

  return Array.from({length: ticks}, (_, index) => {
    const value = maxPercent - (step * index);
    return `${Number(value.toFixed(2))}%`;
  });
}




function transformValues(value:any, decimals:any, sizes:any) {
  const dm = decimals || 2;
  if (value <= 1) {
    return value.toFixed(dm) + ' ' + sizes[0];
  }
  const k = 1000;
  const i = Math.floor(Math.log(value) / Math.log(k));
  return parseFloat((value / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

function createPercentageArray(length:any) {
  if (length < 1) {
    return [];
  }

  if (length === 1) {
    return [0];
  }

  const step = 100 / (length - 1);
  const result = [];

  for (let i = 0; i < length; i++) {
    result.push(i * step);
  }

  return result;
}

export function createPercentageArrays(data: Measure[], fieldName: keyof Measure): [number[], number[], string, number][] {
  if (!data.length) {
    return [];
  }
  
  const maxPacketsIn = Math.max(...data.map((measure) => measure.packets_in));
  const maxPacketsOut = Math.max(...data.map((measure) => measure.packets_out));
  const maxBytesIn = Math.max(...data.map((measure) => measure.bytes_in));
  const maxBytesOut = Math.max(...data.map((measure) => measure.bytes_out));


  const packetsStep = getNetworkScaleMaximum(Math.max(maxPacketsIn, maxPacketsOut));
  const bytesStep = getNetworkScaleMaximum(Math.max(maxBytesIn, maxBytesOut));

  const isBytes = fieldName === 'bytes_in' || fieldName === 'bytes_out'
  const percentValues = createPercentageArray(data.length);
  const resultArrays: any = data.map((measure:any, index:any) => {
    const percentValue = measure[fieldName] === 0 ? 0 : (measure[fieldName] / (isBytes ? bytesStep : packetsStep)) * 100;

    return [percentValues[index], percentValue, measure.date, measure[fieldName].toFixed(2)];
  });
  return resultArrays;
}

// export function createPercentageArrays(data: Measure[], fieldName: keyof Measure): [number[], number[], string, number][] {
//   const maxResultLength = 20;
  
//   // Проверяем, нужно ли ограничивать количество элементов
//   const shouldLimit = data.length > 100;
//   const step = shouldLimit ? Math.max(1, Math.ceil(data.length / maxResultLength)) : 1;
//   const shortenedData = shouldLimit ? data.filter((_, index) => index % step === 0).slice(0, maxResultLength) : data;

//   const maxFieldValue = Math.max(...data.map((measure: any) => measure[fieldName]));

//   const maxPacketsIn = Math.max(...data.map((measure) => measure.packets_in));
//   const maxPacketsOut = Math.max(...data.map((measure) => measure.packets_out));
//   const maxBytesIn = Math.max(...data.map((measure) => measure.bytes_in));
//   const maxBytesOut = Math.max(...data.map((measure) => measure.bytes_out));

//   const packetsStep = Math.max(maxPacketsIn, maxPacketsOut);
//   const bytesStep = Math.max(maxBytesIn, maxBytesOut);

//   const isBytes = fieldName === 'bytes_in' || fieldName === 'bytes_out';
//   const resultArrays: any = shortenedData.map((measure: any, index: any) => {
//     const percentValues = createPercentageArray(shortenedData?.length);
//     const percentValue =
//       measure[fieldName] === 0 ? 0 : (measure[fieldName] / (isBytes ? bytesStep : packetsStep)) * 100;

//     return [percentValues[index], percentValue, measure.date, measure[fieldName].toFixed(2)];
//   });
//   console.log('resultArrays', resultArrays);
//   return resultArrays;
// }
